import {
  ghostBtnClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { ComposeNode } from "@/lib/manufacturing/composePlan";

/** Partial-commit picker for ready product SKUs. */
export function ManufacturingModelsComposeCommitStep({
  readyProducts,
  selectedCommitIds,
  stubCount,
  selectedProductCount,
  commitLog,
  pendingSandboxCodes,
  busy,
  onToggle,
  onBack,
  onCommit,
}: {
  readyProducts: readonly ComposeNode[];
  selectedCommitIds: readonly string[];
  stubCount: number;
  selectedProductCount: number;
  commitLog: string | null;
  pendingSandboxCodes: readonly string[];
  busy: boolean;
  onToggle: (productId: string, checked: boolean) => void;
  onBack: () => void;
  onCommit: () => void;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm">
        Commit only products whose slots are fully matched or marked theoretical. Unfinished SKUs
        stay in local memory so you can continue later. Confirmations improve suggestions for the
        rest.
      </p>
      {commitLog ? (
        <p className="text-xs text-emerald-800 dark:text-emerald-200">{commitLog}</p>
      ) : null}
      <div className="max-h-48 space-y-1 overflow-y-auto">
        {readyProducts.length === 0 ? (
          <p className="text-xs text-slate-500">
            No products are ready yet. Go back and finish matching slots on at least one SKU.
          </p>
        ) : (
          readyProducts.map((p) => {
            const checked = selectedCommitIds.includes(p.id);
            return (
              <label
                key={p.id}
                className="flex items-center gap-2 rounded border border-slate-200 px-2 py-1.5 text-sm dark:border-zinc-700"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => onToggle(p.id, e.target.checked)}
                />
                <span className="font-medium">{p.code}</span>
                <span className="text-xs text-slate-500">{p.name}</span>
              </label>
            );
          })
        )}
      </div>
      <p className="text-xs text-slate-500">
        Will create {stubCount} theoretical stub(s) and process {selectedProductCount} selected
        product(s).
      </p>
      {pendingSandboxCodes.length > 0 ? (
        <p className="text-xs text-amber-800 dark:text-amber-200">
          After commit, BOM sandbox will open for: {pendingSandboxCodes.join(", ")}
        </p>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={ghostBtnClass} onClick={onBack}>
          Back to matching
        </button>
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={busy || selectedProductCount === 0}
          onClick={onCommit}
        >
          {busy ? "Committing…" : `Commit selected (${selectedProductCount})`}
        </button>
      </div>
    </div>
  );
}
